







































































import { Vue, Component, Prop } from "vue-property-decorator";
import { CardState } from "@/utils/ProjectOverviewUtils";

import {
  containsDryingInfo,
  doesProjectTypeIdHaveDrying,
  getProjectTypeIdOfProject,
  isFlexProject,
} from "@/data/models/ProjectDetails";
import DryingProgressComponent from "./DryingProgress.vue";
import ProjectFinishedComponent from "./ProjectsFinished.vue";
import { DryingProjectOverview } from "@/data/models/DryingProjectOverview";
import LeakDetectionProjectOverview from "@/data/models/LeakDetectionProjectOverview";
import { 
  COLOR_INFO_TEXT_RED, 
  COLOR_INFO_TEXT_GREEN, 
  COLOR_INFO_TEXT_APPOINTMENT_BACKGROUND, 
  COLOR_WHITE_TEXT,
  COLOR_STANDARD_WHITE,
  COLOR_DARK_FILL_OUTLINE_BACKGROUND,
  COLOR_DARK_FILL_OUTLINE_BORDER,
  COLOR_DARK_FILL_OUTLINE_TEXT,
} from "@/utils/colors";
import { themedFill, themedFillTextColor } from "@/utils/Theme";
import { getLastestProjectState } from "@/data/models/ProjectState";
import {
  formatProjectStepInfo,
  ColoredText,
  ColorOption,
  LabelAndStatus,
} from "@/utils/ProjectStepFormatter";
import { isMissingReports } from "@/data/models/ProjectOverview";

@Component({
  components: {
    "drying-progress": DryingProgressComponent,
    "project-finished": ProjectFinishedComponent,
  },
})
export default class ProjectOverviewStatusComponent extends Vue {
  @Prop() readonly cards!: Array<CardState>;
  @Prop() readonly projectOverview!:
    | DryingProjectOverview
    | LeakDetectionProjectOverview;

  @Prop({ default: true }) readonly showProjectStatusCards!: boolean;
  @Prop() readonly showProjectFinishedText!: boolean;
  @Prop() readonly wasFinalReportSend!: boolean;
  @Prop() readonly projectFinishedText!: string;

  today = new Date();

  /**
   * A step not started yet comes in as white (`getCardColor`). Dark mode draws it as an empty
   * outline instead of a fill, so it can never be mistaken for the grey locked tile.
   */
  isNotStartedOutline(cardState: CardState): boolean {
    return this.$vuetify.theme.dark && cardState.color === COLOR_STANDARD_WHITE;
  }

  /**
   * The tile's fill: the dark palette shade of its status colour in dark mode, unchanged in light
   * mode. Called from the template, so a live theme switch repaints it.
   */
  tileColor(cardState: CardState): string {
    return this.isNotStartedOutline(cardState)
      ? COLOR_DARK_FILL_OUTLINE_BACKGROUND
      : themedFill(cardState.color);
  }

  /** The number's colour (dark on a palette fill in dark mode), or the outline of a not-started step. */
  tileStyle(cardState: CardState): Record<string, string> {
    if (this.isNotStartedOutline(cardState)) {
      return {
        border: `1px solid ${COLOR_DARK_FILL_OUTLINE_BORDER}`,
        color: COLOR_DARK_FILL_OUTLINE_TEXT,
        boxShadow: "none",
      };
    }

    return { color: themedFillTextColor(cardState.color, "") };
  }

  get dryingProjectOverview() {
    const projectOverview = this.projectOverview;

    return containsDryingInfo(projectOverview)
      ? (projectOverview as DryingProjectOverview)
      : null;
  }

  get showDryingProgress() {
    return !!this.estimatedDryingDuration;
  }

  get estimatedDryingDuration() {
    const dryingProjectOverview = this.dryingProjectOverview;

    if (!dryingProjectOverview) {
      return undefined;
    }

    if (!dryingProjectOverview.setupDryingStartedBy) {
      return undefined;
    }

    if (dryingProjectOverview.dryingEnd) {
      return undefined;
    }

    return dryingProjectOverview.estimatedDryingDurationInDays ?? dryingProjectOverview.dryingProjectSteps?.damageAssessmentResult?.estimatedDryingTime;
  }

  showNoGap(index: number) {
    return index === 7;
  }

  showSmallGap(index: number) {
    return !this.showBigGap(index);
  }

  showBigGap(index: number) {
    if (isFlexProject(this.projectOverview)) {
      return index === 3;
    } else {
      return containsDryingInfo(this.projectOverview)
        ? index === 2
        : index === 3;
    }
  }

  getStyle(coloredText: ColoredText) {
    switch (coloredText.color) {
      case ColorOption.RED_TEXT:
        return `color: ${COLOR_INFO_TEXT_RED}`;
      case ColorOption.GREEN_TEXT:
        return `color: ${COLOR_INFO_TEXT_GREEN}`;
      case ColorOption.RED_BACKGROUND:
        // A missing appointment is a status, so dark mode gives it the status red, not the action red.
        return `color: ${themedFillTextColor(COLOR_INFO_TEXT_APPOINTMENT_BACKGROUND, COLOR_WHITE_TEXT, "status")}; border-radius: 4px; background: ${themedFill(COLOR_INFO_TEXT_APPOINTMENT_BACKGROUND, "status")}; padding: 4px`;
    }

    return "";
  }

  get projectTypeId() {
    return this.projectOverview?.projectTypeId ?? getProjectTypeIdOfProject(this.projectOverview);
  }

  get containsDryingInfo() {
    return doesProjectTypeIdHaveDrying(this.projectTypeId);
  }

  get labelAndStatus(): LabelAndStatus | null {
    const projectOverview = this.projectOverview;

    if (!projectOverview) {
      return null;
    }

    const showApartmentInfo = (projectOverview.contactPersons?.length ?? 0) > 1;
    const lastCompletedStep = getLastestProjectState(
      projectOverview,
      this.today
    );

    if (lastCompletedStep) {
      const labelAndStatus = formatProjectStepInfo(this, lastCompletedStep, showApartmentInfo, this.containsDryingInfo);

      if (
        labelAndStatus &&
        !labelAndStatus.status &&
        isMissingReports(this.projectOverview)
      ) {
        labelAndStatus.status = {
          text: this.$t("projectStep.noReport").toString(),
          color: ColorOption.RED_TEXT,
        };
      }

      return labelAndStatus;
    }

    return null;
  }
}
